import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { type AxiosError } from 'axios';
import { useGetBranch } from '~/hooks';
import { ROUTES } from '~/utils/routes';
import { type EditBranchFormFieldValues, editBranchSchema } from '../logics/schema';
import { EditBranchFormPresentation } from '../presentations';
import { usePutBranch } from './usePutBranch';

type Args = {
  branchId: number;
};

type PresentationProps = React.ComponentProps<typeof EditBranchFormPresentation>;

type IUseEditBranchForm = (args: Args) => {
  state: {
    methods: UseFormReturn<EditBranchFormFieldValues>;
    isSubmitDisabled: PresentationProps['isSubmitDisabled'];
    title: PresentationProps['title'];
  };
  actions: {
    handleSubmit: PresentationProps['onSubmit'];
  };
};

export const useEditBranchForm: IUseEditBranchForm = ({ branchId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode() || '';

  const { data } = useGetBranch({ threeLetterCode, branchId });

  const methods = useForm<EditBranchFormFieldValues>({
    defaultValues: {
      name: data.branch.name,
      code: data.branch.code,
    },
    criteriaMode: 'all',
    mode: 'onChange',
    resolver: zodResolver(editBranchSchema),
  });

  const { trigger, isMutating } = usePutBranch(threeLetterCode, branchId, {
    onSuccess: () => {
      showToast({
        message: '事業所を更新しました。',
        status: 'success',
      });
      navigate(ROUTES.BRANCHES.INDEX);
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        if (errors && errors.length > 0) {
          showToast({
            message: errors[0],
            status: 'danger',
          });
          return;
        }
      }

      showToast({
        message: '事業所の更新に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(formData => {
    trigger({
      branch: {
        name: formData.name,
        code: formData.code,
      },
    });
  });

  return {
    state: {
      methods,
      isSubmitDisabled: !methods.formState.isValid || isMutating,
      title: `${data.branch.name}の編集`,
    },
    actions: {
      handleSubmit,
    },
  };
};
